import React, { useState, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import { Loader2, ArrowDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  className?: string;
  threshold?: number;
  disabled?: boolean;
}

export function PullToRefresh({
  children,
  onRefresh,
  className,
  threshold = 80,
  disabled = false,
}: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const pullDistance = useMotionValue(0);
  const controls = useAnimation();

  const indicatorOpacity = useTransform(pullDistance, [0, threshold / 2, threshold], [0, 0.5, 1]);
  const indicatorScale = useTransform(pullDistance, [0, threshold], [0.5, 1]);
  const indicatorRotation = useTransform(pullDistance, [0, threshold], [0, 180]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing) return;
      const container = containerRef.current;
      if (container && container.scrollTop === 0) {
        startY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    },
    [disabled, isRefreshing]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling || disabled || isRefreshing) return;
      const container = containerRef.current;
      if (container && container.scrollTop === 0) {
        const currentY = e.touches[0].clientY;
        const diff = currentY - startY.current;
        if (diff > 0) {
          // Apply resistance
          const resistance = 0.4;
          pullDistance.set(diff * resistance);
        }
      }
    },
    [isPulling, disabled, isRefreshing, pullDistance]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    setIsPulling(false);

    const currentPull = pullDistance.get();
    if (currentPull >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      await controls.start({ y: threshold });
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        await controls.start({ y: 0 });
        pullDistance.set(0);
      }
    } else {
      await controls.start({ y: 0 });
      pullDistance.set(0);
    }
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh, controls]);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Pull indicator */}
      <motion.div
        className="absolute left-0 right-0 top-0 flex items-center justify-center pointer-events-none z-10"
        style={{
          opacity: indicatorOpacity,
          y: useTransform(pullDistance, (v) => Math.min(v, threshold) - 40),
        }}
      >
        <motion.div
          className="flex items-center justify-center h-10 w-10 rounded-full bg-muted"
          style={{ scale: indicatorScale }}
        >
          {isRefreshing ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <motion.div style={{ rotate: indicatorRotation }}>
              <ArrowDown className="h-5 w-5 text-muted-foreground" />
            </motion.div>
          )}
        </motion.div>
      </motion.div>

      {/* Content */}
      <motion.div
        ref={containerRef}
        className="h-full overflow-auto"
        animate={controls}
        style={{ y: isPulling ? pullDistance : 0 }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default PullToRefresh;
